import { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from '../store/useStore'

const IDLE_TIMEOUT = 30 * 60 * 1000 // 30 minutes

/**
 * Monitors user activity and triggers logout after IDLE_TIMEOUT of inactivity.
 * Listens to mouse, keyboard, touch, and scroll events.
 * Only active when user is logged in.
 */
export function useIdleTimeout() {
  const navigate = useNavigate()
  const { isLoggedIn, logout } = useStore()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleIdle = useCallback(() => {
    if (!isLoggedIn) return
    logout()
    navigate('/login', { replace: true })
  }, [isLoggedIn, logout, navigate])

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (isLoggedIn) {
      timerRef.current = setTimeout(handleIdle, IDLE_TIMEOUT)
    }
  }, [isLoggedIn, handleIdle])

  useEffect(() => {
    if (!isLoggedIn) return

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const
    const handler = () => resetTimer()

    events.forEach((e) => window.addEventListener(e, handler, { passive: true }))
    resetTimer() // Start initial timer

    return () => {
      events.forEach((e) => window.removeEventListener(e, handler))
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [isLoggedIn, resetTimer])
}
